/* CRDSHP homepage sections: slab tilt, product option pickers, image thumbs and tabs.
   No dependencies. Safe to include from several sections; it only boots once and
   re-runs when a section is added or edited in the Shopify theme editor. */
(() => {
  if (window.crdshpSections) return;
  window.crdshpSections = true;

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hero motion: spotlight over the product wall, light wall parallax, and the graded slab.
     The slab follows the cursor across the hero and leans harder when you're on it. With no
     pointer (phones, or the cursor elsewhere) it sways on its own. Runs only while visible. */
  function hero(root) {
    const slab = root.querySelector('[data-cs-tilt]');
    const wall = root.querySelector('.cs-hero__wall');
    const stage = slab && slab.parentElement;
    const rest = { rx: 4, ry: -10, mx: 30, my: 30, s: 1, glare: .35, sx: 72, sy: 42, px: 0, py: 0 };
    const cur = { ...rest }, target = { ...rest };
    const clamp = (v, a = -1, b = 1) => Math.max(a, Math.min(b, v));
    let active = false, visible = true, raf = 0, idleT = 0, gyro = null, baseBeta = null;

    const frame = t => {
      raf = 0;
      if (!visible) return;
      if (!active && gyro) {
        // Phone tilt drives the card and the light.
        target.ry = rest.ry * .3 + gyro.x * 20;
        target.rx = rest.rx * .3 - gyro.y * 14;
        target.mx = 50 + gyro.x * 50; target.my = 50 + gyro.y * 50;
        target.sx = 64 + gyro.x * 30; target.sy = 45 + gyro.y * 30;
        target.glare = .55; target.s = 1;
      } else if (!active) {
        // Idle sway: slow figure-eight for the slab, wandering spotlight.
        idleT = t / 1000;
        target.ry = rest.ry + Math.sin(idleT * .7) * 7;
        target.rx = rest.rx + Math.sin(idleT * 1.1) * 3.5;
        target.mx = 50 + Math.sin(idleT * .7) * 35;
        target.my = 40 + Math.cos(idleT * .9) * 25;
        target.sx = 64 + Math.sin(idleT * .35) * 26;
        target.sy = 45 + Math.cos(idleT * .5) * 30;
        target.px = target.py = 0; target.s = 1; target.glare = .35;
      }
      const k = active ? .16 : .05;
      for (const key in cur) cur[key] += (target[key] - cur[key]) * k;
      if (slab) {
        slab.style.setProperty('--rx', cur.rx.toFixed(2) + 'deg');
        slab.style.setProperty('--ry', cur.ry.toFixed(2) + 'deg');
        slab.style.setProperty('--mx', cur.mx.toFixed(1) + '%');
        slab.style.setProperty('--my', cur.my.toFixed(1) + '%');
        slab.style.setProperty('--s', cur.s.toFixed(3));
        slab.style.setProperty('--glare', cur.glare.toFixed(2));
        if (stage) {
          stage.style.setProperty('--shx', (-cur.ry * 1.1).toFixed(1) + 'px');
          stage.style.setProperty('--shs', (1 - Math.abs(cur.rx) / 60).toFixed(3));
        }
      }
      root.style.setProperty('--sx', cur.sx.toFixed(1) + '%');
      root.style.setProperty('--sy', cur.sy.toFixed(1) + '%');
      if (wall) { wall.style.setProperty('--px', cur.px.toFixed(3)); wall.style.setProperty('--py', cur.py.toFixed(3)); }
      raf = requestAnimationFrame(frame);
    };
    const run = () => { if (!raf && visible && !reduceMotion) raf = requestAnimationFrame(frame); };

    const track = (x, y) => {
      const h = root.getBoundingClientRect();
      target.sx = ((x - h.left) / h.width) * 100;
      target.sy = ((y - h.top) / h.height) * 100;
      target.px = clamp(((x - h.left) / h.width) * 2 - 1);
      target.py = clamp(((y - h.top) / h.height) * 2 - 1);
      if (!slab) return;
      const r = slab.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const on = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      // On the card: position within the card. Off it: distance across the hero, softer.
      const nx = on ? clamp((x - cx) / (r.width / 2)) : clamp((x - cx) / (h.width * .5));
      const ny = on ? clamp((y - cy) / (r.height / 2)) : clamp((y - cy) / (h.height * .6));
      const strength = on ? 1 : .5;
      target.ry = rest.ry * (on ? 0 : .5) + nx * 12 * strength;
      target.rx = rest.rx * (on ? 0 : .5) - ny * 8 * strength;
      target.mx = 50 + nx * 50;
      target.my = 50 + ny * 50;
      target.s = on ? 1.02 : 1;
      target.glare = on ? .48 : .36;
    };

    root.addEventListener('pointermove', e => {
      // Touch only drives the card while a finger is on it, so page scrolling is unaffected.
      if (e.pointerType !== 'mouse' && !(slab && slab.contains(e.target))) return;
      active = true;
      track(e.clientX, e.clientY);
      run();
    });
    const release = () => { active = false; run(); };
    root.addEventListener('pointerleave', release);
    root.addEventListener('pointerup', e => { if (e.pointerType !== 'mouse') release(); });
    root.addEventListener('pointercancel', release);

    new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) run(); }).observe(root);

    // Phone tilt (gyroscope). Android sends it right away; iPhone asks once, on the first tap.
    const onTilt = e => {
      if (e.gamma == null || e.beta == null) return;
      if (baseBeta == null) baseBeta = e.beta;
      gyro = { x: clamp(e.gamma / 30), y: clamp((e.beta - baseBeta) / 30) };
      run();
    };
    let listening = false;
    const listen = () => { if (!listening) { listening = true; addEventListener('deviceorientation', onTilt); } };
    const needsAsk = typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function';
    if (!needsAsk && matchMedia('(pointer: coarse)').matches) listen();

    // Tap / click / Enter flips the card to its back.
    if (slab && slab.matches('[data-cs-flip]')) {
      const flip = () => {
        const on = !slab.classList.contains('is-flipped');
        slab.classList.toggle('is-flipped', on);
        slab.setAttribute('aria-pressed', on);
        slab.querySelector('.cs-slab__face--back')?.setAttribute('aria-hidden', !on);
        if (needsAsk && !listening) DeviceOrientationEvent.requestPermission().then(r => { if (r === 'granted') listen(); }).catch(() => {});
      };
      slab.addEventListener('click', flip);
      slab.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); } });
    }

    // Sticky shop bar (phones): shown once the hero buttons have scrolled away.
    // The bar sits right after the hero section (outside it, so it stacks above later sections).
    const sticky = root.parentElement.querySelector('[data-cs-sticky]');
    const ctas = root.querySelector('.cs-hero__ctas');
    if (sticky && ctas) {
      // Shown after the hero buttons scroll away; hidden again near the bottom so it never covers the footer.
      let past = false, nearEnd = false;
      const sync = () => { sticky.hidden = !past || nearEnd; };
      new IntersectionObserver(([e]) => { past = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(ctas);
      const onScroll = () => { const end = innerHeight + scrollY > document.documentElement.scrollHeight - 700; if (end !== nearEnd) { nearEnd = end; sync(); } };
      addEventListener('scroll', onScroll, { passive: true });
    }
    run();
  }

  /* Color swatches: in-stock colors first, then a thin divider, then sold-out ones, each group in the
     store's order. Only touches the DOM when the order actually changes, so focus isn't lost. */
  function arrangeSwatches(wrap, items, isOut) {
    if (!wrap || !items.length) return;
    let split = wrap.querySelector('.cs-swatch-split');
    if (!split) { split = document.createElement('span'); split.className = 'cs-swatch-split'; split.setAttribute('aria-hidden', 'true'); }
    const ins = items.filter(x => !isOut(x)), outs = items.filter(isOut);
    const order = [...ins, split, ...outs];
    split.hidden = !ins.length || !outs.length;
    if (order.some((el, i) => wrap.children[i] !== el)) wrap.append(...order);
  }

  /* Option buttons -> variant id, price, stock and image. Variant data comes from a
     JSON script tag rendered by the section. */
  function picker(root) {
    const data = root.querySelector('[data-cs-variants]');
    if (!data) return;
    let variants = JSON.parse(data.textContent);
    const start = root.dataset.csStart;
    // Switchable versions (e.g. PSA / BGS / CGC): the buttons swap the variants shown here. A version
    // with nothing in stock can't be picked; if the first one is sold out, start on one that isn't.
    const versionsEl = root.querySelector('[data-cs-versions]'), versions = versionsEl ? JSON.parse(versionsEl.textContent) : null;
    const chips = [...root.querySelectorAll('[data-cs-version]')], more = root.querySelector('[data-cs-more]');
    const inStock = list => list.some(v => v.available);
    let version = 0;
    if (versions && !inStock(versions[0].variants)) { const i = versions.findIndex(x => inStock(x.variants)); if (i > 0) { version = i; variants = versions[i].variants; } }
    const first = (start && variants.find(v => v.available && v.options.includes(start))) || variants.find(v => v.available) || variants[0];
    if (!first) return;
    const sel = first.options.slice();
    const input = root.querySelector('input[name="id"]');
    const price = root.querySelector('[data-cs-price]');
    const button = root.querySelector('[data-cs-add]');
    const image = root.querySelector('[data-cs-variant-image]');
    const matches = (v, s) => v.options.every((o, i) => o === s[i]);
    const colorSwatches = [...root.querySelectorAll('.cs-swatch[data-cs-option]')];

    const update = () => {
      const v = variants.find(x => matches(x, sel));
      root.querySelectorAll('[data-cs-option]').forEach(b => {
        const i = +b.dataset.index, val = b.dataset.value;
        const trial = sel.slice(); trial[i] = val;
        b.setAttribute('aria-pressed', sel[i] === val);
        b.classList.toggle('is-unavailable', !variants.some(x => x.available && matches(x, trial)));
      });
      root.querySelectorAll('[data-cs-selected]').forEach(l => { l.textContent = sel[+l.dataset.csSelected] || ''; });
      if (colorSwatches.length) arrangeSwatches(colorSwatches[0].parentElement, colorSwatches, b => b.classList.contains('is-unavailable'));
      chips.forEach((c, i) => {
        const out = !!versions[i] && !inStock(versions[i].variants);
        c.setAttribute('aria-checked', i === version); c.setAttribute('aria-pressed', i === version);
        c.disabled = out; if (out) c.title = 'Sold out'; else c.removeAttribute('title');
      });
      if (more && versions) more.href = versions[version].url + (v ? '?variant=' + v.id : '');
      if (!v) {
        if (button) { button.disabled = true; button.textContent = button.dataset.unavailable; }
        return;
      }
      if (input) input.value = v.id;
      if (price) price.innerHTML = v.price + (v.compare ? ' <s>' + v.compare + '</s>' : '');
      if (button) {
        button.disabled = !v.available;
        button.textContent = v.available ? button.dataset.add : button.dataset.soldout;
      }
      if (image && v.image) { image.removeAttribute('srcset'); image.src = v.image; }
    };

    root.addEventListener('click', e => {
      const c = e.target.closest('[data-cs-version]');
      if (c) {
        const i = +c.dataset.csVersion, next = versions && versions[i];
        if (!next || !inStock(next.variants)) return;
        version = i; variants = next.variants;
        // Keep the color when it's in stock in this version, otherwise land on the first one that is.
        const keep = variants.find(x => x.available && x.options[0] === sel[0]) || variants.find(x => x.available);
        keep.options.forEach((o, j) => { sel[j] = o; });
        update();
        return;
      }
      const b = e.target.closest('[data-cs-option]');
      if (!b) return;
      const i = +b.dataset.index;
      sel[i] = b.dataset.value;
      // If that combination doesn't exist or is sold out, snap to an available one.
      if (!variants.some(x => x.available && matches(x, sel))) {
        const m = variants.find(x => x.available && x.options[i] === b.dataset.value);
        if (m) m.options.forEach((o, j) => { sel[j] = o; });
      }
      update();
    });
    update();
  }

  /* Gallery thumbnails swap the main image. */
  function thumbs(root) {
    const main = root.querySelector('[data-cs-variant-image]');
    root.querySelectorAll('[data-cs-thumb]').forEach(b => b.addEventListener('click', () => {
      main.removeAttribute('srcset');
      main.src = b.dataset.csThumb;
      root.querySelectorAll('[data-cs-thumb]').forEach(x => x.setAttribute('aria-pressed', x === b));
    }));
  }

  /* Accessible tabs. */
  function tabs(root) {
    const list = [...root.querySelectorAll('[role="tab"]')];
    const show = tab => list.forEach(t => {
      const on = t === tab;
      t.setAttribute('aria-selected', on);
      t.tabIndex = on ? 0 : -1;
      document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
    });
    list.forEach((t, i) => {
      t.addEventListener('click', () => show(t));
      t.addEventListener('keydown', e => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (d) { const n = list[(i + d + list.length) % list.length]; show(n); n.focus(); }
      });
    });
  }

  /* Article: reading progress, share and copy link. */
  function article(root) {
    const prose = root.querySelector('[data-cs-prose]');
    const bar = document.querySelector('[data-cs-progress]');
    if (bar && prose) {
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = prose.getBoundingClientRect();
        const done = Math.min(1, Math.max(0, (innerHeight - r.top) / (r.height + innerHeight * .2)));
        bar.style.transform = 'scaleX(' + done.toFixed(3) + ')';
      };
      addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    }
    root.querySelectorAll('[data-cs-share]').forEach(b => {
      if (!navigator.share) { b.hidden = true; return; }
      b.addEventListener('click', () => navigator.share({ title: b.dataset.title, url: b.dataset.url }).catch(() => {}));
    });
    root.querySelectorAll('[data-cs-copy]').forEach(b => b.addEventListener('click', () => {
      const done = () => { const t = b.textContent; b.textContent = 'Link copied'; setTimeout(() => { b.textContent = t; }, 1800); };
      const fail = () => { b.textContent = b.dataset.csCopy; };
      (navigator.clipboard ? navigator.clipboard.writeText(b.dataset.csCopy) : Promise.reject()).then(done, fail);
    }));
  }

  // Always open a page at the top, even when the browser would restore an old scroll position.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  addEventListener('pageshow', () => { if (!location.hash || location.hash.length < 2) scrollTo(0, 0); });

  /* Site-wide cursor light: one listener, mouse only. Sets the glow position on the dark section
     under the cursor and the sheen position on the card or photo under it. */
  const SHEEN = '.cs-card__media, .cs-shopby__img, .cs-news-card__media, .cs-post__media, .cs-review, .cs-drop__main, .cs-colors__stage, .cs-tile, .cs-feature, .cs-pillar';
  if (!reduceMotion && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let lit = null, sheen = null, tilted = null, pulled = null, ev = null, queued = false;
    const apply = () => {
      queued = false;
      const t = ev.target instanceof Element ? ev.target : null;
      const sec = t && t.closest('.cs--ink:not(.cs-hero)');
      if (sec !== lit) { if (lit) lit.classList.remove('is-lit'); if (sec) sec.classList.add('is-lit'); lit = sec; }
      if (sec) { const r = sec.getBoundingClientRect(); sec.style.setProperty('--gx', (ev.clientX - r.left) + 'px'); sec.style.setProperty('--gy', (ev.clientY - r.top) + 'px'); }
      const el = t && t.closest(SHEEN);
      if (el !== sheen) { if (sheen) sheen.classList.remove('is-sheen'); if (el) el.classList.add('is-sheen'); sheen = el; }
      if (el !== tilted) { if (tilted) { tilted.style.removeProperty('--tx'); tilted.style.removeProperty('--ty'); } tilted = el; }
      if (el) {
        const r = el.getBoundingClientRect(); const x = ev.clientX - r.left, y = ev.clientY - r.top;
        el.style.setProperty('--mx', x + 'px'); el.style.setProperty('--my', y + 'px');
        // Lean toward the cursor; smaller elements lean a little more.
        const max = r.width > 500 ? 2 : 5;
        el.style.setProperty('--ty', ((x / r.width - .5) * 2 * max).toFixed(2) + 'deg');
        el.style.setProperty('--tx', ((.5 - y / r.height) * 2 * max).toFixed(2) + 'deg');
      }
      // Magnetic buttons (not the sticky bar).
      const btn = t && t.closest('.cs-btn');
      const mag = btn && !btn.closest('.cs-sticky') ? btn : null;
      if (mag !== pulled) { if (pulled) { pulled.style.removeProperty('--bx'); pulled.style.removeProperty('--by'); } pulled = mag; }
      if (mag) { const r = mag.getBoundingClientRect(); mag.style.setProperty('--bx', ((ev.clientX - r.left - r.width / 2) * .18).toFixed(1) + 'px'); mag.style.setProperty('--by', ((ev.clientY - r.top - r.height / 2) * .3).toFixed(1) + 'px'); }
      ring.classList.toggle('is-link', !!(t && t.closest('a, button, [role="button"], summary, input, select, label')));
    };
    // Cursor ring: eases toward the pointer every frame, so it glides and never jumps. On the first
    // move it starts exactly at the pointer; it fades out when the mouse leaves the window.
    const ring = document.createElement('div'); ring.className = 'cs-cursor'; ring.setAttribute('aria-hidden', 'true');
    document.body.appendChild(ring);
    const pos = { x: 0, y: 0, tx: 0, ty: 0, live: false, raf: 0 };
    const glide = () => {
      pos.x += (pos.tx - pos.x) * .24; pos.y += (pos.ty - pos.y) * .24;
      ring.style.setProperty('--cx', pos.x.toFixed(1) + 'px'); ring.style.setProperty('--cy', pos.y.toFixed(1) + 'px');
      pos.raf = Math.abs(pos.tx - pos.x) + Math.abs(pos.ty - pos.y) > .2 ? requestAnimationFrame(glide) : 0;
    };
    document.addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse') return;
      pos.tx = e.clientX; pos.ty = e.clientY;
      if (!pos.live) { pos.live = true; pos.x = pos.tx; pos.y = pos.ty; ring.style.setProperty('--cx', pos.x + 'px'); ring.style.setProperty('--cy', pos.y + 'px'); }
      ring.classList.add('is-on');
      if (!pos.raf) pos.raf = requestAnimationFrame(glide);
    }, { passive: true });
    document.addEventListener('pointerdown', () => ring.classList.add('is-down'));
    document.addEventListener('pointerup', () => ring.classList.remove('is-down'));
    document.documentElement.addEventListener('pointerleave', () => { ring.classList.remove('is-on'); pos.live = false; });
    document.addEventListener('pointermove', e => { if (e.pointerType !== 'mouse') return; ev = e; if (!queued) { queued = true; requestAnimationFrame(apply); } }, { passive: true });
  }

  // Pause CSS animations in sections that are off screen (see .is-offscreen in crdshp.css).
  const sleeper = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => e.target.classList.toggle('is-offscreen', !e.isIntersecting)), { rootMargin: '200px 0px' }) : null;

  /* Article banners: move into the story before the named heading, color / grader pickers,
     add to cart in place (updates the theme's cart count), and a pointer tilt on desktop. */
  function pban(root) {
    const prose = document.querySelector('[data-cs-prose]'), want = (root.dataset.before || '').trim().toLowerCase()
    if (prose && want) {
      const h = [...prose.querySelectorAll('h1, h2, h3, h4')].find(x => x.textContent.trim().toLowerCase().includes(want))
      if (h) h.before(root)
    }
    const panel = root.querySelector('[data-cs-pban-panel]'), stage = root.querySelector('[data-cs-pban-stage]')
    const img = root.querySelector('[data-cs-pban-img]'), form = root.querySelector('[data-cs-pban-form]')
    const idIn = root.querySelector('[data-cs-pban-id]'), price = root.querySelector('[data-cs-pban-price]'), more = root.querySelector('[data-cs-pban-more]')
    const add = root.querySelector('[data-cs-pban-add]'), note = root.querySelector('[data-cs-pban-note]'), valueLabel = root.querySelector('[data-cs-pban-value]')
    const dataEl = root.querySelector('[data-cs-pban-data]'), list = dataEl ? JSON.parse(dataEl.textContent) : null
    const swatches = [...root.querySelectorAll('[data-cs-pban-color]')], chips = [...root.querySelectorAll('[data-cs-pban-product]')]
    let prod = Math.max(0, chips.findIndex(c => c.getAttribute('aria-checked') === 'true'))
    let color = (swatches.find(s => s.getAttribute('aria-checked') === 'true') || swatches[0] || {}).dataset?.csPbanColor
    // Stock rules: a version with nothing in stock can't be picked, and switching version keeps the
    // color only when it's in stock there, otherwise it lands on the first color that is.
    const inStock = p => p.variants.some(x => x.a)
    const landOn = (p, want) => (p.variants.find(x => x.o === want && x.a) || p.variants.find(x => x.a) || p.variants.find(x => x.o === want) || p.variants[0]).o
    if (list) {
      if (!inStock(list[prod] || list[0])) { const i = list.findIndex(inStock); if (i >= 0) prod = i }
      color = landOn(list[prod] || list[0], color)
    }

    let wantSrc = ''   // the photo the latest click asked for; slower earlier loads are ignored
    const swap = src => { // cross-fade to the new variant photo once it has loaded
      if (!img || !src) return
      wantSrc = src; if (img.src === src) return
      const pre = new Image(); pre.onload = () => { if (wantSrc !== src) return; img.classList.add('is-leaving'); setTimeout(() => { if (wantSrc !== src) return; img.removeAttribute('srcset'); img.src = src; img.classList.remove('is-leaving') }, 180) }; pre.src = src
    }
    const update = () => {
      if (!list) return
      const p = list[prod] || list[0], v = p.variants.find(x => x.o === color) || p.variants[0]
      idIn.value = v.id; price.textContent = v.p; more.href = p.url + (p.url.includes('?') ? '&' : '?') + 'variant=' + v.id
      add.disabled = !v.a; add.textContent = v.a ? add.dataset.add : add.dataset.soldout; add.classList.remove('is-added'); note.textContent = ''
      if (valueLabel) valueLabel.textContent = color
      swatches.forEach(s => { const on = s.dataset.csPbanColor === color; s.setAttribute('aria-checked', on); s.classList.toggle('is-out', !(p.variants.find(x => x.o === s.dataset.csPbanColor) || {}).a) })
      if (swatches.length) arrangeSwatches(swatches[0].parentElement, swatches, s => s.classList.contains('is-out'))
      chips.forEach((c, i) => { c.setAttribute('aria-checked', i === prod); const out = !!list[i] && !inStock(list[i]); c.disabled = out; if (out) c.title = 'Sold out'; else c.removeAttribute('title') })
      swap(v.img)
    }
    swatches.forEach(s => s.addEventListener('click', () => { color = s.dataset.csPbanColor; update() }))
    chips.forEach((c, i) => c.addEventListener('click', () => { if (!list[i] || !inStock(list[i])) return; prod = i; color = landOn(list[i], color); update() }))
    if (list) update()

    form.addEventListener('submit', async e => {
      e.preventDefault(); if (add.disabled) return
      add.disabled = true
      try {
        await addToCart(form)
        add.textContent = add.dataset.added; add.classList.add('is-added')
        note.innerHTML = '<a href="' + cartPath(form) + '">View cart</a>'
      } catch (err) { note.textContent = err.message || 'Could not add. Try again.'; add.textContent = add.dataset.add }
      add.disabled = false
    })

    if (!reduceMotion && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let raf = 0, ev
      panel.addEventListener('pointerenter', () => { panel.classList.remove('is-glint'); void panel.offsetWidth; panel.classList.add('is-glint') })
      panel.addEventListener('pointermove', e => { ev = e; if (!raf) raf = requestAnimationFrame(() => { raf = 0; const r = panel.getBoundingClientRect(); panel.style.setProperty('--mx', ((ev.clientX - r.left) / r.width - .5).toFixed(3)); panel.style.setProperty('--my', ((ev.clientY - r.top) / r.height - .5).toFixed(3)) }) })
      panel.addEventListener('pointerleave', () => { panel.style.setProperty('--mx', 0); panel.style.setProperty('--my', 0) })
    }
  }

  /* Add to cart the way the theme does it: ask for the cart drawer sections back and fire the theme's
     add event, so the drawer, its footer and the bag count all update (and the drawer opens if the
     theme is set to). Without the drawer, the bag count is refreshed from /cart.js. */
  const cartPath = form => form.getAttribute('action').replace(/\/add\/?$/, '')
  async function addToCart(form) {
    const body = new FormData(form)
    const drawer = window.theme && window.theme.settings && window.theme.settings.cartType === 'modal'
    if (drawer) { body.append('sections', 'cart-items,cart-footer,cart-item-count'); body.append('sections_url', location.pathname) }
    const r = await fetch(form.getAttribute('action').replace(/\/?$/, '') + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body })
    const data = await r.json().catch(() => ({}))
    if (!r.ok || data.status) throw new Error(data.description || data.message || 'Could not add. Try again.')
    if (drawer && data.sections) {
      if (!form.id) form.id = 'cs-form-' + Math.random().toString(36).slice(2)
      document.body.dispatchEvent(new CustomEvent('shapes:cart:afteradditem', { bubbles: true, detail: { response: data, sourceId: form.id } }))
    } else {
      fetch(cartPath(form) + '.js').then(x => x.json()).then(cart => { try { window.Alpine && Alpine.store('cart_count') && (Alpine.store('cart_count').count = cart.item_count) } catch (err) {} }).catch(() => {})
    }
    return data
  }

  /* Header: the phone menu drawer. */
  function hdr() {
    const drawer = document.querySelector('[data-cs-drawer]'), open = document.querySelector('[data-cs-drawer-open]')
    if (!drawer || !open) return
    const set = on => {
      drawer.classList.toggle('is-open', on); drawer.setAttribute('aria-hidden', !on); open.setAttribute('aria-expanded', on)
      document.documentElement.style.overflow = on ? 'hidden' : ''
      if (on) drawer.querySelector('[data-cs-drawer-close]').focus(); else open.focus({ preventScroll: true })
    }
    open.addEventListener('click', () => set(true))
    drawer.querySelector('[data-cs-drawer-close]').addEventListener('click', () => set(false))
    drawer.querySelectorAll('a').forEach(a => a.addEventListener('click', () => set(false)))
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) set(false) })
  }

  /* Product page: option buttons pick the variant (snapping to an available match), price, button
     and URL follow it, and Add to bag adds in place. */
  function pdp(root) {
    const dataEl = root.querySelector('[data-cs-pdp-data]')
    const variants = dataEl ? JSON.parse(dataEl.textContent) : []
    const btns = [...root.querySelectorAll('[data-cs-pdp-opt]')]
    const form = root.querySelector('[data-cs-pdp-form], form[action*="/cart/add"]'), idIn = root.querySelector('[data-cs-pdp-id]')
    const price = root.querySelector('[data-cs-pdp-price]'), add = root.querySelector('[data-cs-pdp-add]'), note = root.querySelector('[data-cs-pdp-note]')
    const sel = []
    btns.forEach(b => { if (b.getAttribute('aria-checked') === 'true') sel[+b.dataset.csPdpOpt] = b.dataset.value })
    const match = opts => variants.find(v => v.options.every((o, i) => o === opts[i]))
    const draw = () => {
      const v = match(sel)
      btns.forEach(b => {
        const i = +b.dataset.csPdpOpt, trial = sel.slice(); trial[i] = b.dataset.value
        b.setAttribute('aria-checked', sel[i] === b.dataset.value)
        b.classList.toggle('is-out', !variants.some(x => x.available && x.options.every((o, j) => o === trial[j])))
      })
      root.querySelectorAll('[data-cs-pdp-value]').forEach(s => { s.textContent = sel[+s.dataset.csPdpValue] || '' })
      add.classList.remove('is-added'); note.textContent = ''
      if (!v) { add.disabled = true; add.textContent = add.dataset.unavailable; return }
      idIn.value = v.id
      price.textContent = v.price + ' '
      if (v.compare) { const s = document.createElement('s'); s.textContent = v.compare; price.append(s) }
      add.disabled = !v.available; add.textContent = v.available ? add.dataset.add : add.dataset.soldout
      history.replaceState(history.state, '', location.pathname + '?variant=' + v.id)
    }
    btns.forEach(b => b.addEventListener('click', () => {
      const i = +b.dataset.csPdpOpt; sel[i] = b.dataset.value
      if (!(match(sel) || {}).available) { const m = variants.find(x => x.available && x.options[i] === b.dataset.value); if (m) m.options.forEach((o, j) => { sel[j] = o }) }
      draw()
    }))
    if (form) form.addEventListener('submit', async e => {
      e.preventDefault(); if (add.disabled) return
      add.disabled = true
      try {
        await addToCart(form)
        add.textContent = 'Added'; add.classList.add('is-added')
        note.innerHTML = '<a href="' + cartPath(form) + '">View cart</a>'
      } catch (err) { note.textContent = err.message; add.textContent = add.dataset.add }
      add.disabled = false
    })
  }

  /* Collection page: search filters the products on the page, sort reloads in the chosen order. */
  function shop(root) {
    const input = root.querySelector('[data-cs-shop-search]'), sort = root.querySelector('[data-cs-shop-sort]')
    const grid = document.querySelector('[data-cs-shop-grid]'), count = document.querySelector('[data-cs-shop-count]'), empty = document.querySelector('[data-cs-shop-empty]')
    if (sort) sort.addEventListener('change', () => { const u = new URL(location.href); u.searchParams.set('sort_by', sort.value); u.searchParams.delete('page'); location.href = u.toString() })
    if (!input || !grid) return
    const items = [...grid.querySelectorAll('[data-title]')], total = count ? count.textContent : ''
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase()
      let n = 0
      items.forEach(el => { const hit = !q || el.dataset.title.includes(q); el.hidden = !hit; if (hit) n++ })
      if (count) count.textContent = q ? n + ' ' + (n === 1 ? count.dataset.one : count.dataset.many) + ' for “' + input.value.trim() + '”' : total
      if (empty) empty.hidden = n > 0
    })
  }

  function boot(scope) {
    const once = (sel, fn) => scope.querySelectorAll(sel).forEach(el => {
      if (el.dataset.csReady) return;
      el.dataset.csReady = '1';
      fn(el);
    });
    if (sleeper) scope.querySelectorAll('.cs').forEach(el => sleeper.observe(el));
    once('[data-cs-hero]', hero);
    once('[data-cs-product]', el => { picker(el); thumbs(el); });
    once('[data-cs-tabs]', tabs);
    once('[data-cs-pban]', pban);
    once('[data-cs-hdr]', hdr);
    once('[data-cs-pdp]', pdp);
    once('[data-cs-filters]', shop);
    once('[data-cs-article]', article);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
